import { OUTDOOR_ANIMAL_BY_ID, OUTDOOR_NPC_BY_ID } from '@/lib/village-map';

/** Ready crop art, keyed by crop id (also the Hmong word id). */
const CROP_READY: Record<string, string> = {
  nplej: 'riceReady',
  zaub: 'zaubReady',
  'pob-kws': 'cornReady',
  'kua-txob': 'chiliReady',
  taum: 'taumReady',
  taub: 'taubReady',
  'txiv-lws': 'lwsReady',
};

/** Species id → atlas cell. Word ids are the Hmong names players discover. */
const FISH_BY_ID: Record<string, string> = {
  minnow: 'fish_minnow',
  carp: 'fish_carp',
  catfish: 'fish_catfish',
  perch: 'fish_perch',
  gourami: 'fish_gourami',
  loach: 'fish_loach',
  snakehead: 'fish_snakehead',
  'freshwater-eel': 'fish_freshwater_eel',
  knifefish: 'fish_knifefish',
  'freshwater-stingray': 'fish_freshwater_stingray',
  betta: 'fish_betta',
  'golden-carp': 'fish_golden_carp',
  'ancient-golden-carp': 'fish_ancient_golden_carp',
  'giant-river-catfish': 'fish_giant_river_catfish',
  'giant-snakehead': 'fish_giant_snakehead',
  'spotted-knifefish': 'fish_spotted_knifefish',
  'moonlight-knifefish': 'fish_moonlight_knifefish',
  'giant-freshwater-stingray': 'fish_giant_freshwater_stingray',
  'rainbow-stingray': 'fish_rainbow_stingray',
};

const FISH_BY_WORD: Record<string, string> = {
  'ntses-me': 'fish_minnow',
  'ntses-liab': 'fish_carp',
  'ntses-nwj': 'fish_catfish',
  'ntses-kab': 'fish_perch',
  'ntses-siab': 'fish_gourami',
  'ntses-av': 'fish_loach',
  'ntses-hau': 'fish_snakehead',
  'ntses-nab': 'fish_freshwater_eel',
  'ntses-riam': 'fish_knifefish',
  'ntses-duav': 'fish_freshwater_stingray',
  'ntses-ntaus': 'fish_betta',
  'ntses-kub': 'fish_golden_carp',
  'ntses-kub-qub': 'fish_ancient_golden_carp',
  'ntses-nwj-loj': 'fish_giant_river_catfish',
  'ntses-hau-loj': 'fish_giant_snakehead',
  'ntses-riam-nplaim': 'fish_spotted_knifefish',
  'ntses-riam-hli': 'fish_moonlight_knifefish',
  'ntses-duav-loj': 'fish_giant_freshwater_stingray',
  'ntses-duav-zaj': 'fish_rainbow_stingray',
  ntses: 'fishRipple',
};

const FORAGE_KIND: Record<string, string> = {
  bush: 'berryBushFull',
  tree: 'fruitTreeFull',
};

const FORAGE_WORD: Record<string, string> = {
  'txiv-pos': 'berryBushFull',
  'txiv-ntoo': 'fruitTreeFull',
};

const ORE: Record<string, string> = {
  'ore:av': 'mountDirt',
  'ore:zeb': 'rockFull',
  'gem:emerald': 'emerald',
};

const AREAS: Record<string, string> = {
  town: 'well',
  farm: 'paddy',
  passage: 'bamboo',
  mountain: 'mountPeak',
  mine: 'mineEntrance',
  largeTown: 'roofM',
  greatCity: 'bridge',
};

const AVATARS: Record<string, string> = {
  boy: 'player_down0',
  girl: 'player_f_down0',
};

/** Cloud-save XP ledger keys. */
const XP_SOURCES: Record<string, string> = {
  word: 'tipSign',
  quest: 'npcPog',
  harvest: 'riceReady',
  catch: 'fish_carp',
  mine: 'rockFull',
  forage: 'berryBushFull',
  gift: 'mailbox',
  daily_objective: 'sign',
  all_quests: 'npcYawg',
};

const QUESTS: Record<string, string> = {
  'quest-catch-fish': 'fish_carp',
  'quest-qaib': 'chicken0',
  'quest-npua': 'pig0',
  'quest-twm': 'buffalo',
  'quest-harvest': 'riceReady',
  'quest-berries': 'berryBushFull',
  'quest-zaub': 'zaubReady',
};

/** Words that are not already a crop, animal, fish, or forage id. */
const EXTRA_WORDS: Record<string, string> = {
  dej: 'waterJug',
  av: 'mountDirt',
  zeb: 'rockFull',
  emerald: 'emerald',
  roob: 'mountPeak',
  'qhov-zeb': 'mineEntrance',
  nyiaj: 'stall',
  mov: 'riceReady',
  'hav-zoov': 'tree',
};

const SKIP = new Set(['', 'none', 'unset', 'unknown', '(none)', '(unknown)']);

function fishSprite(id: string): string | undefined {
  return FISH_BY_ID[id];
}

/**
 * Atlas sprite for an analytics row key, when the key is a village thing
 * (crop, fish, item, npc, area, quest, word). Level numbers and platforms
 * stay text.
 */
export function villageSpriteForKey(key: string): string | undefined {
  const raw = key.trim();
  if (SKIP.has(raw)) return undefined;

  if (raw.startsWith('seed:')) return 'seedSack';
  if (raw.startsWith('produce:')) return CROP_READY[raw.slice('produce:'.length)];
  if (raw.startsWith('fish:')) return fishSprite(raw.slice('fish:'.length));
  if (raw.startsWith('forage:')) return FORAGE_WORD[raw.slice('forage:'.length)];
  if (raw.startsWith('ore:') || raw.startsWith('gem:')) return ORE[raw];
  if (raw.startsWith('rod ')) return 'toolFishingRod';
  if (raw.startsWith('quest-')) {
    return QUESTS[raw] ?? villageSpriteForKey(raw.slice('quest-'.length));
  }

  return (
    OUTDOOR_NPC_BY_ID[raw]?.sprite ??
    OUTDOOR_ANIMAL_BY_ID[raw]?.sprite ??
    CROP_READY[raw] ??
    fishSprite(raw) ??
    FISH_BY_WORD[raw] ??
    FORAGE_KIND[raw] ??
    FORAGE_WORD[raw] ??
    AREAS[raw] ??
    AVATARS[raw] ??
    XP_SOURCES[raw] ??
    EXTRA_WORDS[raw]
  );
}

/** Idle cast beside the page title. */
export const VILLAGE_HEADER_CAST = [
  'npcPog',
  'npcYawg',
  'npcNiam',
  'player_down0',
  'player_f_down0',
] as const;
